import React, { useState, useEffect } from "react";
import Typography from "../../atoms/typography";
import Chip from "../../atoms/chip";
import AddItemButton from "./addItemButton";
import { useSelector, useDispatch } from "react-redux";
import { handleSelectedAddon } from "../../../store/slices/common/addOn";

import { RECOMMENDED } from "../../../constants/common/common";
import { updateCurrency } from "../../../utils/common";
import isEmpty from "lodash.isempty";
import PropTypes from "prop-types";
import CustomImage from "../../atoms/customImage";
import { cleverTapCommonEvents } from "../../../analytics/clevertapConstants";
import useAnalytics from "../../../analytics/useAnalytics";
import { ga4addonSelected } from "../../../analytics/gaEvents";
import { GA4_EVENTS } from "../../../analytics/gaConstant";

/**
 * Component that renders the main body for add-ons, displaying
 * available add-ons and managing user interactions with them.
 *
 * @returns {JSX.Element} The rendered add-on body component.
 */
const AddonBody = ({ commonCleverTapData }) => {
  const dispatch = useDispatch();
  const { trackEvent } = useAnalytics();
  const [activeCategory, setActiveCategory] = useState("All");
  const selectedCurrency = useSelector((state) => state?.currencyList?.userSelectedCurrency);
  const { addOnInfo, selectedAddons } = useSelector((state) => state.addOn);
  const checkLoginStatus = useSelector((state) => state.fus.loginDetail?.loggedin);
  const pincode = useSelector((state) => state?.locationLock?.selectedDomesticLocation?.pincode);
  const selectedCartProduct = useSelector((state) => state?.cart?.selectedCartProduct);
  const addonsList = addOnInfo.addonsList?.[0]?.products;
  const imageDomain = process.env.NEXT_PUBLIC_IMAGE_DOMAIN;
  const addonViewAndClose = {
    ...commonCleverTapData,
    is_date_today: null,
    is_date_tomorrow: null,
    is_variant_selected: null,
  };

  useEffect(() => {
    let selectedAddOn = [];
    let addOn = selectedCartProduct?.addons;

    if (!isEmpty(addOn) && !isEmpty(addonsList)) {
      addonsList?.forEach((element1) => {
        addOn?.forEach((element2) => {
          if (element1.productId === element2.productId) {
            selectedAddOn.push({
              ...element1,
              quantity: element2.quantity,
              itemIndex: element2.itemIndex,
              isFromCart: true,
            });
          }
        });
      });
    }

    dispatch(handleSelectedAddon(selectedAddOn));
  }, [selectedCartProduct, addonsList]);

  useEffect(() => {
    if (!isEmpty(addonsList)) {
      trackEvent(cleverTapCommonEvents.addonViewed, addonViewAndClose, ["clevertap"]);
    } else {
      trackEvent(cleverTapCommonEvents.addonNotViewed, addonViewAndClose, ["clevertap"]);
    }
  }, []);

  const organizedAddons = addOnInfo.allOrganizedAddonsList;

  const categories = ["All", ...(organizedAddons?.map((item) => item[Object.keys(item)[0]].categoryName) || [])];

  const filteredAddons =
    activeCategory === "All"
      ? addonsList
      : addonsList.filter((item) =>
          organizedAddons.some(
            (category) =>
              category[Object.keys(category)[0]].categoryName === activeCategory &&
              category[Object.keys(category)[0]].addOnProductIds.includes(item.productId),
          ),
        );

  /**
   * Handles the selection of a category and updates the active category.
   *
   * @param {string} category - The selected category to filter the addon products.
   */
  const getSelectedItemQuantity = (productId) => {
    const selectedItem = selectedAddons.find((item) => item.productId === productId);
    return selectedItem ? selectedItem.quantity : 0;
  };

  /**
   * Updates the quantity of a selected add-on or adds it to the list if it is not found.
   *
   * @param {Object} addon - The add-on item whose quantity is to be changed.
   * @param {number} count - The new quantity for the add-on.
   */
  const handleItemCountChange = (addon, count) => {
    let isFound = false;

    const value = selectedAddons.map((item) => {
      if (item.productId === addon.productId) {
        isFound = true;
        if (item.quantity < count) {
          trackEvent(GA4_EVENTS?.ADDON_SELECTED, { ...ga4addonSelected(pincode, checkLoginStatus, "PDP", "Plus") }, [
            "ga4",
          ]);
        } else {
          trackEvent(GA4_EVENTS?.ADDON_SELECTED, { ...ga4addonSelected(pincode, checkLoginStatus, "PDP", "Minus") }, [
            "ga4",
          ]);
        }
        return {
          ...item,
          quantity: count,
        };
      }
      return item;
    });

    if (isFound) {
      dispatch(handleSelectedAddon(value));
    } else {
      dispatch(handleSelectedAddon([...selectedAddons, { ...addon, quantity: count }]));
      trackEvent(
        cleverTapCommonEvents.addonAdded,
        {
          ...commonCleverTapData,
          addon_name: addon?.productName,
          addon_price: addon?.price,
          addon_quantity: count,
          addon_type: addon?.productType,
          is_date_today: null,
          is_date_tomorrow: null,
          is_variant_selected: null,
        },
        ["clevertap"],
      );
      trackEvent(GA4_EVENTS?.ADDON_SELECTED, { ...ga4addonSelected(pincode, checkLoginStatus, "PDP", "Plus") }, [
        "ga4",
      ]);
    }
  };

  /**
   * Removes an add-on item from the selected add-ons list.
   *
   * @param {Object} addon - The add-on item to be removed, identified by its productId.
   */
  const onRemoveItem = (addon) => {
    let value = [];
    selectedAddons.forEach((item) => {
      if (item.isFromCart && item.productId == addon.productId) {
        value.push({ ...item, quantity: 0 });
      } else {
        if (item.productId !== addon.productId) {
          value.push(item);
        }
      }
    });

    dispatch(handleSelectedAddon(value)); // Remove from selectedAddons
    trackEvent(
      cleverTapCommonEvents.addonRemoved,
      {
        ...commonCleverTapData,
        addon_price: addon?.price,
        addon_name: addon?.productName,
        addon_quantity: addon?.configItemIndex,
        is_date_today: null,
        is_date_tomorrow: null,
        is_variant_selected: null,
      },
      ["clevertap"],
    );
    trackEvent(GA4_EVENTS?.ADDON_SELECTED, { ...ga4addonSelected(pincode, checkLoginStatus, "PDP", "Minus") }, ["ga4"]);
  };

  /**
   * Updates the active category based on the user's selection.
   *
   * @param {string} category - The category selected by the user.
   */
  const onCategorySelection = (category) => {
    setActiveCategory(category);
  };

  return (
    <>
      <div className="category scrollbar-hide sticky top-0 z-50 flex h-[12%] w-full gap-8 overflow-x-auto border-b-1 border-greySoft bg-white-900 px-[16px] py-10 md:min-h-[60px] md:border-b-0 md:pb-0 md:pt-12 md:scrollbar-thin">
        {categories?.map((category) => (
          <Chip
            id={category}
            key={category}
            content={category}
            onClick={() => onCategorySelection(category)}
            size="medium"
            color="default"
            variant="default"
            classes={`cursor-pointer border rounded border-1 h-32 text-12 font-500 ease-in-out duration-300 ${activeCategory === category ? "bg-fnp-100 text-fnp-400 border-darkOliveGreen" : "bg-white-900 border-greySoft text-grey-400 hover:border-grey"} `}
          />
        ))}
      </div>
      <div
        className={`scrollbar-hide flex h-[100vh] flex-wrap justify-between overflow-x-auto overflow-y-auto px-[16px] pb-[35vh] pt-16 md:justify-start md:gap-20 md:pb-0 md:pt-8 md:scrollbar-thin`}
      >
        {filteredAddons?.map((item) => {
          return (
            <div
              key={item.productId}
              className="relative mb-16 flex w-[48%] flex-col items-start p-2 md:w-[14.9%] xxl:w-[15.5%]"
            >
              {item.recommended && (
                <div className="bg-black text-white absolute left-2 top-2 z-10 rounded px-2 py-1 text-xs">
                  {RECOMMENDED}
                </div>
              )}

              <div className="mb-2 flex w-full items-center justify-center overflow-hidden rounded-lg border border-[#EEEEEE]">
                <CustomImage
                  src={imageDomain + item.imageUrl}
                  height={160}
                  width={160}
                  isPriority={false}
                  alt={item.productName}
                  customStyle={{
                    width: "100%",
                  }}
                  sizes="100vw"
                />
              </div>

              <Typography
                variant="body2"
                Tag="p"
                classes="w-[100%] overflow-hidden truncate text-ellipsis pt-8 text-12 font-500"
              >
                {item.productName}
              </Typography>

              <div className="flex  w-full items-center justify-between pt-8">
                <div className="text-14 font-600 text-fnp-500">
                  {updateCurrency(selectedCurrency, item.price)}
                  {item.listPrice > item.price && (
                    <span className="ml-8 text-12 font-400 text-gray-500 line-through">
                      {updateCurrency(selectedCurrency, item.listPrice)}
                    </span>
                  )}
                </div>

                <div>
                  <AddItemButton
                    onRemoveItem={(count) => onRemoveItem(item, count)}
                    selectedItem={getSelectedItemQuantity(item.productId)} // Fetch current quantity
                    setSelectedItem={(count) => handleItemCountChange(item, count)} // Update quantity
                    className="ml-auto"
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};
AddonBody.propTypes = {
  commonCleverTapData: PropTypes.object,
};

export default AddonBody;
